import { isTruckCompatibilityActionable } from "./resultsFleetData.js";

const ASPIRATOR_INCOMPATIBLE_REASON_CODE = "aspirator_required_no_compatible_truck";
const ONTARIO_INCOMPATIBLE_REASON_CODE = "ontario_required_no_compatible_truck";
const DISPATCH_DISABLED_REASON_CODE = "truck_dispatch_disabled";
const LOCKED_TRUCK_MISMATCH_REASON_CODE = "locked_truck_mismatch";
const LIVE_REFRESH_REASON_CODES = new Set([
  LOCKED_TRUCK_MISMATCH_REASON_CODE,
  "local_rebuild_check_pending",
]);
const LEGACY_PRODUCT_REASON_CODES = new Set([
  "locked_product_incompatible",
  "product_incompatible",
  "truck_product_locked",
]);

function normalizeTextValue(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isMergedOperationalMissionSegment(segment = null) {
  return Boolean(
    segment?.isSameAddressLoadingDeliveryMerged === true
    || segment?.isAspiratorMissionMerged === true
  );
}

function normalizeProductKey(value) {
  return normalizeTextValue(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function firstNonEmptyText(...values) {
  for (const value of values) {
    const normalizedValue = normalizeTextValue(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
}

function normalizeBooleanText(value) {
  return normalizeTextValue(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function isTruthyLike(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  return ["1", "o", "oui", "true", "vrai", "yes"].includes(normalizeBooleanText(value));
}

function isFalseLike(value) {
  if (typeof value === "boolean") {
    return !value;
  }
  if (typeof value === "number") {
    return value === 0;
  }
  return ["0", "n", "non", "false", "faux", "no"].includes(normalizeBooleanText(value));
}

function resolveBooleanLike(values = [], fallback = false) {
  for (const value of Array.isArray(values) ? values : [values]) {
    if (isTruthyLike(value)) {
      return true;
    }
    if (isFalseLike(value)) {
      return false;
    }
  }
  return fallback;
}

// Le produit associé vient du seul champ du Data Manager, jamais d'une copie d'import (truck_raw_data).
function resolveTruckPreferredProduct(truckCapability = null) {
  return firstNonEmptyText(truckCapability?.preferredGrainType, truckCapability?.preferred_grain_type);
}

function isTruckPreferredForProduct(truckCapability = null, productLabel = "") {
  const preferredProductKey = normalizeProductKey(resolveTruckPreferredProduct(truckCapability));
  return Boolean(preferredProductKey && preferredProductKey === normalizeProductKey(productLabel));
}

function hasPreferredTruckForProduct(truckCapabilities = [], productLabel = "") {
  const normalizedProduct = normalizeProductKey(productLabel);
  if (!normalizedProduct) {
    return false;
  }

  // Comme au serveur, un camion désactivé pour ce dispatch ou inactif ne compte pas dans le parc du produit.
  return (Array.isArray(truckCapabilities) ? truckCapabilities : []).some((truckCapability) => (
    !isTruckDispatchDisabled(truckCapability)
    && truckCapability?.isActive !== false
    && isTruckPreferredForProduct(truckCapability, normalizedProduct)
  ));
}

function buildAssociatedProductFilterMessage(productLabel = "") {
  const normalizedProduct = normalizeTextValue(productLabel);
  return normalizedProduct
    ? `Seuls les camions associés au produit ${normalizedProduct} restent affichés pour cette commande.`
    : "Seuls les camions associés au produit de la commande restent affichés.";
}

function hasTruckAspirator(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object" ? truckCapability.truckRawData : null);

  return resolveBooleanLike([
    truckCapability?.aspirator,
    rawData?.aspirator,
    rawData?.Aspirateur,
  ], false);
}

function hasTruckOntario(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object" ? truckCapability.truckRawData : null);

  return resolveBooleanLike([
    truckCapability?.ontario,
    rawData?.ontario,
    rawData?.Ontario,
  ], false);
}

function hasTruckInitialPreloadEnabled(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object" ? truckCapability.truckRawData : null);

  return resolveBooleanLike([
    truckCapability?.isInitialPreloadEnabled,
    truckCapability?.is_initial_preload_enabled,
    rawData?.is_initial_preload_enabled,
    rawData?.pool_preload,
    rawData?.["Pool preload"],
    rawData?.["Pool Preload"],
  ], true);
}

function isTruckProtected(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object" ? truckCapability.truckRawData : null);

  return resolveBooleanLike([
    truckCapability?.isTruckProtected,
    truckCapability?.is_truck_protected,
    rawData?.is_truck_protected,
  ], false);
}

function isTruckDispatchDisabled(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object" ? truckCapability.truckRawData : null);

  return resolveBooleanLike([
    truckCapability?.isTruckDispatchDisabled,
    truckCapability?.is_dispatch_disabled,
    rawData?.is_dispatch_disabled,
  ], false);
}

export function isTruckDompeurType(truckCapability = null) {
  const rawData = truckCapability?.truck_raw_data && typeof truckCapability.truck_raw_data === "object"
    ? truckCapability.truck_raw_data
    : (truckCapability?.truckRawData && typeof truckCapability.truckRawData === "object"
      ? truckCapability.truckRawData
      : (truckCapability?.raw_data && typeof truckCapability.raw_data === "object" ? truckCapability.raw_data : null));

  if (resolveBooleanLike([
    truckCapability?.isDompeur,
    truckCapability?.is_dompeur,
    rawData?.is_dompeur,
    rawData?.Dompeur,
  ], false)) {
    return true;
  }

  const truckType = firstNonEmptyText(
    truckCapability?.truckType,
    truckCapability?.truck_type,
    truckCapability?.type,
    rawData?.truck_type,
    rawData?.type,
    rawData?.Type,
  );
  return normalizeProductKey(truckType) === "dompeur";
}

function normalizeCompatibilityReasonCode(compatibility) {
  if (typeof compatibility?.reason_code === "string") {
    return compatibility.reason_code.trim().toLowerCase();
  }

  if (typeof compatibility?.reasonCode === "string") {
    return compatibility.reasonCode.trim().toLowerCase();
  }

  return "";
}

function enrichLockedTruckMismatchWithDompeurOverride(compatibility, {
  sourceOrderDompeurRequirements = [],
  truckCapabilityById = null,
} = {}) {
  if (
    normalizeCompatibilityReasonCode(compatibility) !== LOCKED_TRUCK_MISMATCH_REASON_CODE
    || compatibility?.is_product_compatible === false
    || !Array.isArray(sourceOrderDompeurRequirements)
    || sourceOrderDompeurRequirements.length === 0
    || sourceOrderDompeurRequirements.some((value) => typeof value !== "boolean")
  ) {
    return compatibility;
  }

  const targetTruckCapability = truckCapabilityById?.[compatibility?.truck_id] || null;
  if (!targetTruckCapability) {
    return compatibility;
  }

  const targetIsDompeur = isTruckDompeurType(targetTruckCapability);
  if (!sourceOrderDompeurRequirements.some((sourceIsDompeur) => sourceIsDompeur !== targetIsDompeur)) {
    return compatibility;
  }

  return {
    ...compatibility,
    confirmation_code: "dompeur_override",
    detail: targetIsDompeur
      ? "Attention: ce camion dompeur est normalement réservé aux commandes Dompeur. Cette réaffectation contourne cette règle."
      : "Attention: cette commande Dompeur est normalement réservée aux camions dompeur. Cette réaffectation contourne cette règle.",
    is_assignable: true,
    is_product_compatible: true,
    reason_code: "dompeur_override_required",
    reason_label: "Type de camion incompatible",
    requires_confirmation: true,
    tone: "warning",
  };
}

function hasLiveRefreshCompatibilityConflict(contexts = []) {
  return (Array.isArray(contexts) ? contexts : []).some((context) => (
    Array.isArray(context?.compatibilities)
      && context.compatibilities.some((compatibility) => (
        compatibility
        && !compatibility.is_current_truck
        && LIVE_REFRESH_REASON_CODES.has(normalizeCompatibilityReasonCode(compatibility))
      ))
  ));
}

function filterVisibleCompatibilities(compatibilities = [], { requiresAspirator = false, requiresOntario = false, sourceProductLabel = "", truckCapabilityById = null } = {}) {
  const aspiratorFilteredTruckIds = new Set();
  const associatedProductFilteredTruckIds = new Set();
  const ontarioFilteredTruckIds = new Set();
  const protectedFilteredTruckIds = new Set();
  const normalizedSourceProduct = normalizeTextValue(sourceProductLabel);
  const sourceProductHasAssociatedTruck = hasPreferredTruckForProduct(
    Object.values(truckCapabilityById || {}),
    normalizedSourceProduct,
  );

  const visibleCompatibilities = (Array.isArray(compatibilities) ? compatibilities : []).filter((compatibility) => {
    const reasonCode = normalizeCompatibilityReasonCode(compatibility);
    if (
      !compatibility
      || compatibility.is_current_truck
      || (compatibility.is_product_compatible === false && !LEGACY_PRODUCT_REASON_CODES.has(reasonCode))
      || typeof compatibility.truck_id !== "string"
      || !compatibility.truck_id
    ) {
      return false;
    }

    const truckCapability = truckCapabilityById?.[compatibility.truck_id] || null;
    if (isTruckProtected(truckCapability)) {
      protectedFilteredTruckIds.add(compatibility.truck_id);
      return false;
    }

    if (reasonCode === ASPIRATOR_INCOMPATIBLE_REASON_CODE) {
      aspiratorFilteredTruckIds.add(compatibility.truck_id);
      return false;
    }

    if (reasonCode === ONTARIO_INCOMPATIBLE_REASON_CODE) {
      ontarioFilteredTruckIds.add(compatibility.truck_id);
      return false;
    }

    if (requiresAspirator) {
      if (truckCapability && !hasTruckAspirator(truckCapability)) {
        aspiratorFilteredTruckIds.add(compatibility.truck_id);
        return false;
      }
    }

    if (requiresOntario) {
      if (truckCapability && !hasTruckOntario(truckCapability)) {
        ontarioFilteredTruckIds.add(compatibility.truck_id);
        return false;
      }
    }

    if (
      normalizedSourceProduct
      && sourceProductHasAssociatedTruck
      && truckCapability
      && !isTruckPreferredForProduct(truckCapability, normalizedSourceProduct)
    ) {
      associatedProductFilteredTruckIds.add(compatibility.truck_id);
      return false;
    }

    return true;
  });

  const blockedReasonByTruckId = {};
  (Array.isArray(compatibilities) ? compatibilities : []).forEach((compatibility) => {
    const truckId = compatibility?.truck_id;
    if (!truckId || typeof truckId !== "string" || compatibility.is_current_truck || visibleCompatibilities.includes(compatibility)) {
      return;
    }
    blockedReasonByTruckId[truckId] = {
      truckName: truckCapabilityById?.[truckId]?.truckLabel || normalizeTextValue(compatibility.truck_name) || truckId,
      reason: isDispatchDisabledCompatibility(compatibility, truckCapabilityById) ? "Désactivé pour ce dispatch."
        : protectedFilteredTruckIds.has(truckId) ? "Camion protégé."
        : aspiratorFilteredTruckIds.has(truckId) ? "Sans aspirateur."
        : ontarioFilteredTruckIds.has(truckId) ? "Pas autorisé Ontario."
        : associatedProductFilteredTruckIds.has(truckId) ? `Pas associé au produit ${normalizedSourceProduct}.`
        : describeBlockedCompatibility(compatibility, truckCapabilityById),
    };
  });

  return {
    associatedProductFilteredTruckCount: associatedProductFilteredTruckIds.size,
    aspiratorFilteredTruckCount: aspiratorFilteredTruckIds.size,
    blockedReasonByTruckId,
    compatibilities: visibleCompatibilities,
    ontarioFilteredTruckCount: ontarioFilteredTruckIds.size,
    protectedFilteredTruckCount: protectedFilteredTruckIds.size,
  };
}

function isDispatchDisabledCompatibility(compatibility, truckCapabilityById = null) {
  return normalizeCompatibilityReasonCode(compatibility) === DISPATCH_DISABLED_REASON_CODE
    || isTruckDispatchDisabled(truckCapabilityById?.[compatibility.truck_id]);
}

// Raison courte d'un camion grisé, montrée au survol pendant le glisser.
function describeBlockedCompatibility(compatibility, truckCapabilityById = null) {
  if (isDispatchDisabledCompatibility(compatibility, truckCapabilityById)) {
    return "Désactivé pour ce dispatch.";
  }
  const label = normalizeTextValue(compatibility.reason_label).replace(/\.$/, "");
  return label ? `${label}.` : "Non compatible.";
}

function addBlockedCompatibilityReasons(blockedReasonByTruckId, compatibilities, actionableCompatibilities, truckCapabilityById = null) {
  compatibilities
    .filter((compatibility) => !actionableCompatibilities.includes(compatibility))
    .forEach((compatibility) => {
      blockedReasonByTruckId[compatibility.truck_id] = {
        truckName: truckCapabilityById?.[compatibility.truck_id]?.truckLabel || normalizeTextValue(compatibility.truck_name) || compatibility.truck_id,
        reason: describeBlockedCompatibility(compatibility, truckCapabilityById),
      };
    });
  return blockedReasonByTruckId;
}

function buildCompatibilityIndex(compatibilities = []) {
  return (Array.isArray(compatibilities) ? compatibilities : []).reduce((index, compatibility) => {
    index[compatibility.truck_id] = compatibility;
    return index;
  }, {});
}

function countDispatchDisabledVisibleTruckIds(compatibilities = [], truckCapabilityById = null) {
  const truckIds = new Set();
  (Array.isArray(compatibilities) ? compatibilities : []).forEach((compatibility) => {
    const truckId = normalizeTextValue(compatibility?.truck_id);
    if (!truckId) {
      return;
    }
    if (
      normalizeCompatibilityReasonCode(compatibility) === DISPATCH_DISABLED_REASON_CODE
      || isTruckDispatchDisabled(truckCapabilityById?.[truckId])
    ) {
      truckIds.add(truckId);
    }
  });

  return truckIds.size;
}

function isGroupedReassignmentCompatibilityActionable(compatibility) {
  if (
    !compatibility
    || compatibility.is_current_truck
    || typeof compatibility.truck_id !== "string"
    || !compatibility.truck_id
  ) {
    return false;
  }

  if (isTruckCompatibilityActionable(compatibility)) {
    return true;
  }

  // « Hors dispatch » et « Camion à choisir » (truck_inactive) bloquent aussi un groupe, quelle que soit sa composition.
  return ![DISPATCH_DISABLED_REASON_CODE, "truck_inactive"].includes(normalizeCompatibilityReasonCode(compatibility));
}

function buildNoCompatibleTruckMessage({
  associatedProductFilteredTruckCount = 0,
  aspiratorFilteredTruckCount = 0,
  ontarioFilteredTruckCount = 0,
  protectedFilteredTruckCount = 0,
  requiresAspirator = false,
  requiresOntario = false,
  sourceProductLabel = "",
  sourceTruckName = "",
} = {}) {
  if (protectedFilteredTruckCount > 0) {
    return "Les camions protégés sont exclus du glisser-déposer. Retirez d'abord la protection du camion cible pour autoriser une réaffectation.";
  }

  if (requiresAspirator && aspiratorFilteredTruckCount > 0) {
    const sourceLabel = normalizeTextValue(sourceTruckName) || "Le camion source";
    return `Cette commande exige un camion aspirateur. ${sourceLabel} est actuellement le seul camion visible avec aspirateur. Déposez la commande dans la zone Suspendues pour la retirer de la tournée.`;
  }

  if (requiresOntario && ontarioFilteredTruckCount > 0) {
    const sourceLabel = normalizeTextValue(sourceTruckName) || "Le camion source";
    return `Cette commande Ontario exige un camion autorisé Ontario. ${sourceLabel} est actuellement le seul camion visible autorisé Ontario. Déposez la commande dans la zone Suspendues pour la retirer de la tournée.`;
  }

  if (normalizeTextValue(sourceProductLabel) && associatedProductFilteredTruckCount > 0) {
    return `${buildAssociatedProductFilterMessage(sourceProductLabel)} Déposez la commande dans la zone Suspendues pour la retirer de la tournée.`;
  }

  return "Aucun camion compatible n'est disponible pour ce segment. Déposez la commande dans la zone Suspendues pour la retirer de la tournée.";
}

function buildCandidatePoolMessage({
  actionableCompatibilities = [],
  associatedProductFilteredTruckCount = 0,
  aspiratorFilteredTruckCount = 0,
  compatibilities = [],
  dispatchDisabledVisibleTruckCount = 0,
  ontarioFilteredTruckCount = 0,
  protectedFilteredTruckCount = 0,
  requiresAspirator = false,
  requiresOntario = false,
  sourceProductLabel = "",
  sourceTruckName = "",
} = {}) {
  if (!compatibilities.length) {
    return buildNoCompatibleTruckMessage({
      aspiratorFilteredTruckCount,
      associatedProductFilteredTruckCount,
      ontarioFilteredTruckCount,
      protectedFilteredTruckCount,
      requiresAspirator,
      requiresOntario,
      sourceProductLabel,
      sourceTruckName,
    });
  }

  if (actionableCompatibilities.length > 0) {
    return "";
  }

  if (protectedFilteredTruckCount > 0) {
    return "Les camions protégés sont exclus du glisser-déposer. Les autres lignes restent visibles pour signaler les contraintes encore bloquantes.";
  }

  if (dispatchDisabledVisibleTruckCount > 0) {
    return dispatchDisabledVisibleTruckCount === compatibilities.length
      ? "Les camions visibles sont désactivés pour ce dispatch. Réactivez d'abord un camion cible avant de glisser-déposer cette commande."
      : "Les camions désactivés pour ce dispatch restent visibles mais sont exclus du glisser-déposer tant qu'ils ne sont pas réactivés.";
  }

  if (requiresAspirator && aspiratorFilteredTruckCount > 0) {
    return "Seuls les camions avec aspirateur restent affichés. Les autres lignes sont exclues car cette commande exige un camion aspirateur.";
  }

  if (requiresOntario && ontarioFilteredTruckCount > 0) {
    return "Seuls les camions autorisés Ontario restent affichés. Les autres lignes sont exclues car cette commande est Ontario.";
  }

  if (normalizeTextValue(sourceProductLabel) && associatedProductFilteredTruckCount > 0) {
    return buildAssociatedProductFilterMessage(sourceProductLabel);
  }

  return "Camions admissibles affichés. Les contraintes métier ou techniques encore bloquantes restent visibles sur ces lignes.";
}

export function buildReassignmentVisibleTruckCapabilityIndex(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((index, row) => {
    const assignment = row?.assignment && typeof row.assignment === "object"
      ? row.assignment
      : null;
    const truckId = normalizeTextValue(row?.assignmentId) || normalizeTextValue(assignment?.truck_id);

    if (!truckId) {
      return index;
    }

    const truckRawData = assignment?.truck_raw_data && typeof assignment.truck_raw_data === "object"
      ? assignment.truck_raw_data
      : null;

    index[truckId] = {
      aspirator: normalizeTextValue(assignment?.aspirator),
      ontario: normalizeTextValue(assignment?.ontario),
      isInitialPreloadEnabled: resolveBooleanLike([
        assignment?.is_initial_preload_enabled,
        truckRawData?.is_initial_preload_enabled,
        truckRawData?.pool_preload,
        truckRawData?.["Pool preload"],
        truckRawData?.["Pool Preload"],
      ], true),
      isDompeur: resolveBooleanLike([
        assignment?.is_dompeur,
        truckRawData?.is_dompeur,
        truckRawData?.Dompeur,
      ], false),
      isTruckDispatchDisabled: Boolean(row?.isDispatchDisabled ?? assignment?.is_dispatch_disabled),
      isActive: assignment?.is_active !== false,
      isTruckProtected: Boolean(row?.isTruckProtected ?? assignment?.is_truck_protected),
      preferredGrainType: firstNonEmptyText(assignment?.preferred_grain_type),
      truckType: firstNonEmptyText(assignment?.truck_type, assignment?.type, truckRawData?.truck_type, truckRawData?.type, truckRawData?.Type),
      truckName: normalizeTextValue(assignment?.truck_name) || normalizeTextValue(row?.truckLabel) || truckId,
      // Nom affiché par le Gantt (« W27 » pour une ligne « Camion à choisir ») : bulle « non éligible » du glisser.
      truckLabel: normalizeTextValue(row?.truckLabel),
      truck_raw_data: truckRawData,
    };
    return index;
  }, {});
}

export function getTimelineSegmentActionKey(assignmentId = "", segment = null) {
  const safeAssignmentId = typeof assignmentId === "string" ? assignmentId : "";
  const safeTripId = typeof segment?.tripId === "string" ? segment.tripId : "";
  const safeSequence = Number.isFinite(Number(segment?.actionSegmentSequence))
    ? String(segment.actionSegmentSequence)
    : (Number.isFinite(Number(segment?.globalSequence)) ? String(segment.globalSequence) : "");

  if (!safeAssignmentId || !safeTripId || !safeSequence) {
    return "";
  }

  return `${safeAssignmentId}::${safeTripId}::${safeSequence}`;
}

export function findSegmentContextByActionKey(rows = [], segmentActionKey = "") {
  if (!segmentActionKey) {
    return null;
  }

  for (const row of Array.isArray(rows) ? rows : []) {
    for (const segment of Array.isArray(row?.segments) ? row.segments : []) {
      if (getTimelineSegmentActionKey(row?.assignmentId || "", segment) === segmentActionKey) {
        return { row, segment };
      }
    }
  }

  return null;
}

export function mergeTimelineSegmentActionPresentation(actionSegment = null, presentationSegment = null) {
  if (presentationSegment?.isGroupActionTarget === true) {
    return presentationSegment;
  }

  if (!actionSegment || !isMergedOperationalMissionSegment(presentationSegment)) {
    return actionSegment;
  }

  const mergedMemberSegments = Array.isArray(presentationSegment?.mergedMemberSegments)
    ? presentationSegment.mergedMemberSegments.filter(Boolean)
    : [];
  if (!mergedMemberSegments.length) {
    return actionSegment;
  }

  return {
    ...actionSegment,
    aspiratorMissionGroupKey: presentationSegment?.aspiratorMissionGroupKey || actionSegment?.aspiratorMissionGroupKey || "",
    isAspiratorMissionMerged: presentationSegment?.isAspiratorMissionMerged === true,
    isSameAddressLoadingDeliveryMerged: presentationSegment?.isSameAddressLoadingDeliveryMerged === true,
    mergedMemberSegments,
    mergedSequenceLabel: presentationSegment?.mergedSequenceLabel || "",
  };
}

export function resolveImmediateDeliveryId(segment = null) {
  if (typeof segment?.businessStep?.suspendButton?.deliveryId === "string" && segment.businessStep.suspendButton.deliveryId) {
    return segment.businessStep.suspendButton.deliveryId;
  }

  if (typeof segment?.businessStep?.dispatcherActionButton?.deliveryId === "string" && segment.businessStep.dispatcherActionButton.deliveryId) {
    return segment.businessStep.dispatcherActionButton.deliveryId;
  }

  if (
    segment?.businessStep?.confirmButton?.type === "delivery"
    && typeof segment.businessStep.confirmButton.targetId === "string"
    && segment.businessStep.confirmButton.targetId
  ) {
    return segment.businessStep.confirmButton.targetId;
  }

  if (typeof segment?.businessStep?.releaseButton?.deliveryId === "string" && segment.businessStep.releaseButton.deliveryId) {
    return segment.businessStep.releaseButton.deliveryId;
  }

  if (typeof segment?.deliveryId === "string" && segment.deliveryId) {
    return segment.deliveryId;
  }

  if (typeof segment?.operationDeliveryId === "string" && segment.operationDeliveryId) {
    return segment.operationDeliveryId;
  }

  return "";
}

export function normalizeGroupDragMembers(groupItem = null, candidateSegment = null) {
  const rawMemberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments : [];
  const mergedCandidateSegments = isMergedOperationalMissionSegment(candidateSegment)
    && Array.isArray(candidateSegment?.mergedMemberSegments)
    ? candidateSegment.mergedMemberSegments.filter(Boolean)
    : [];
  const memberSegments = rawMemberSegments.length
    ? rawMemberSegments
    : (mergedCandidateSegments.length ? mergedCandidateSegments : (candidateSegment ? [candidateSegment] : []));
  const expandedMemberSegments = memberSegments.flatMap((segment) => (
    isMergedOperationalMissionSegment(segment)
    && Array.isArray(segment?.mergedMemberSegments)
    && segment.mergedMemberSegments.length > 0
      ? segment.mergedMemberSegments.filter(Boolean)
      : [segment]
  ));
  const reassignmentSegments = expandedMemberSegments.filter((segment) => (
    segment?.reassignmentDrag?.stepId && segment?.reassignmentDrag?.stepType
  ));
  const actionSegments = expandedMemberSegments.filter((segment) => (
    resolveImmediateDeliveryId(segment)
    && (
      (segment?.businessStep?.stepId && segment?.businessStep?.stepType)
      || (segment?.reassignmentDrag?.stepId && segment?.reassignmentDrag?.stepType)
      || (segment?.segmentReorder?.stepId && segment?.segmentReorder?.stepType)
    )
  ));
  const reorderGroupSegments = expandedMemberSegments.filter((segment) => (
    segment?.segmentReorder?.stepId
    && segment?.segmentReorder?.stepType === candidateSegment?.segmentReorder?.stepType
  ));
  const reorderGroupStepIds = Array.from(new Set(
    reorderGroupSegments.map((segment) => segment.segmentReorder.stepId).filter(Boolean),
  ));
  const seenOrderIds = new Set();
  const deliveryIds = [];
  const orderIds = [];

  for (const segment of actionSegments) {
    const orderId = normalizeTextValue(
      segment?.operationOrderId
      || segment?.businessStep?.orderId
      || segment?.reassignmentDrag?.orderId
      || segment?.segmentReorder?.orderId,
    );
    const deliveryId = resolveImmediateDeliveryId(segment);
    if (!deliveryId) {
      continue;
    }

    const dedupeKey = orderId || deliveryId;
    if (seenOrderIds.has(dedupeKey)) {
      continue;
    }

    seenOrderIds.add(dedupeKey);
    deliveryIds.push(deliveryId);
    if (orderId) {
      orderIds.push(orderId);
    }
  }

  const containsMergedPresentation = mergedCandidateSegments.length > 0
    || memberSegments.some((segment) => isMergedOperationalMissionSegment(segment));
  const memberCount = containsMergedPresentation && deliveryIds.length > 0
    ? deliveryIds.length
    : (Number.isInteger(groupItem?.sameSiteGroupSize) && groupItem.sameSiteGroupSize > 0
        ? groupItem.sameSiteGroupSize
        : memberSegments.length);

  return {
    deliveryIds,
    expandedMemberSegments,
    groupSteps: Array.from(new Map(
      actionSegments
        .map((segment) => ({
          stepId: normalizeTextValue(
            segment?.reassignmentDrag?.stepId
            || segment?.segmentReorder?.stepId
            || segment?.businessStep?.stepId,
          ),
          stepType: normalizeTextValue(
            segment?.reassignmentDrag?.stepType
            || segment?.segmentReorder?.stepType
            || segment?.businessStep?.stepType,
          ),
        }))
        .filter((step) => step.stepId && step.stepType)
        .map((step) => [`${step.stepType}:${step.stepId}`, step]),
    ).values()),
    groupStepIds: reorderGroupStepIds.length > 0
      ? reorderGroupStepIds
      : Array.from(new Set(
          actionSegments.map((segment) => (
            segment?.reassignmentDrag?.stepId
            || segment?.businessStep?.stepId
          )).filter(Boolean),
        )),
    orderIds,
    label: normalizeTextValue(groupItem?.toLabel) || normalizeTextValue(candidateSegment?.toLabel) || "Groupe",
    memberCount,
    memberSegments,
    previewContexts: reassignmentSegments.map((segment) => {
      const contexts = Array.isArray(segment?.reassignmentPreviewContexts)
        ? segment.reassignmentPreviewContexts.filter(Boolean)
        : [];
      return contexts[0] || null;
    }).filter(Boolean),
    requiresAspirator: reassignmentSegments.some((segment) => segment?.requiresAspirator === true),
    requiresOntario: reassignmentSegments.some((segment) => segment?.requiresOntario === true),
    reorderGroupSegments,
    reassignmentSegments,
    sourceGroupKey: normalizeTextValue(groupItem?.groupKey)
      || normalizeTextValue(candidateSegment?.aspiratorMissionGroupKey)
      || normalizeTextValue(candidateSegment?.sameSiteGroupKey),
  };
}

export function buildTimelineGroupActionPresentation(groupItem = null, candidateSegment = null, {
  assignmentId = "",
} = {}) {
  const groupTarget = normalizeGroupDragMembers(groupItem, candidateSegment);
  const representativeSegment = groupTarget.reassignmentSegments[0]
    || groupTarget.expandedMemberSegments[0]
    || candidateSegment
    || null;
  const sourceTruckId = normalizeTextValue(assignmentId || representativeSegment?.assignmentId);

  if (
    !representativeSegment
    || groupTarget.deliveryIds.length < 2
    || groupTarget.groupSteps.length < 2
    || !groupTarget.sourceGroupKey
    || !sourceTruckId
  ) {
    return null;
  }

  return {
    ...representativeSegment,
    assignmentId: sourceTruckId,
    isGroupActionTarget: true,
    sameSiteGroupKey: groupTarget.sourceGroupKey,
    typeLabel: `${groupTarget.memberCount} commande${groupTarget.memberCount > 1 ? "s" : ""}`,
    groupActionTarget: {
      ...groupTarget,
      memberSegments: groupTarget.expandedMemberSegments,
      sourceTruckId,
    },
  };
}

export function resolveSegmentReassignmentGroupDrag(segment = null, explicitGroupDrag = null) {
  if (explicitGroupDrag && typeof explicitGroupDrag === "object") {
    return explicitGroupDrag;
  }

  if (
    !isMergedOperationalMissionSegment(segment)
    || !Array.isArray(segment?.mergedMemberSegments)
    || segment.mergedMemberSegments.length < 2
  ) {
    return null;
  }

  const inferredGroupDrag = normalizeGroupDragMembers(null, segment);
  return inferredGroupDrag.deliveryIds.length > 1 ? inferredGroupDrag : null;
}

function intersectCompatibilityTruckIds(leftTruckIds = null, rightTruckIds = new Set()) {
  if (leftTruckIds === null) {
    return new Set(rightTruckIds);
  }

  return new Set([...leftTruckIds].filter((truckId) => rightTruckIds.has(truckId)));
}

export function buildGroupedReassignmentDragCandidate(contexts = [], options = {}) {
  const safeContexts = (Array.isArray(contexts) ? contexts : []).filter(Boolean);
  if (!safeContexts.length) {
    return {
      errorMessage: "Aucune commande exploitable n'est disponible pour ce groupe dans le flux actuel.",
    };
  }

  if (safeContexts.length === 1) {
    return buildReassignmentDragCandidate(safeContexts, options);
  }

  const rawCandidates = safeContexts.map((context) => buildReassignmentDragCandidate([context], options));
  const failedCandidate = rawCandidates.find((candidate) => !normalizeTextValue(candidate?.deliveryId));
  if (failedCandidate) {
    return {
      errorMessage: failedCandidate.errorMessage || failedCandidate.message || "Aucun camion compatible n'est disponible pour toutes les commandes du groupe.",
    };
  }
  const seenDeliveryIds = new Set();
  const candidates = rawCandidates.filter((candidate) => {
    const deliveryId = normalizeTextValue(candidate?.deliveryId);
    if (!deliveryId || seenDeliveryIds.has(deliveryId)) {
      return false;
    }

    seenDeliveryIds.add(deliveryId);
    return true;
  });

  let intersectedVisibleTruckIds = null;
  const deliveryIds = [];
  let requiresLiveRefresh = false;
  let aspiratorFilteredTruckCount = 0;
  let associatedProductFilteredTruckCount = 0;
  let ontarioFilteredTruckCount = 0;
  let protectedFilteredTruckCount = 0;
  let dispatchDisabledVisibleTruckCount = 0;

  for (const candidate of candidates) {
    deliveryIds.push(normalizeTextValue(candidate.deliveryId));
    intersectedVisibleTruckIds = intersectCompatibilityTruckIds(
      intersectedVisibleTruckIds,
      new Set(Object.keys(candidate.compatibilityByTruckId || {})),
    );
    if (candidate.requiresLiveRefresh) {
      requiresLiveRefresh = true;
    }
    aspiratorFilteredTruckCount += Number(candidate.aspiratorFilteredTruckCount) || 0;
    associatedProductFilteredTruckCount += Number(candidate.associatedProductFilteredTruckCount) || 0;
    ontarioFilteredTruckCount += Number(candidate.ontarioFilteredTruckCount) || 0;
    protectedFilteredTruckCount += Number(candidate.protectedFilteredTruckCount) || 0;
  }

  const compatibilities = candidates.flatMap((candidate) => candidate.compatibilities || [])
    .filter((compatibility, index, items) => (
      compatibility?.truck_id
      && intersectedVisibleTruckIds?.has(compatibility.truck_id)
      && items.findIndex((item) => item?.truck_id === compatibility.truck_id) === index
    ));
  const actionableCompatibilities = compatibilities.filter((compatibility) => (
    isGroupedReassignmentCompatibilityActionable(compatibility)
    && !isTruckDispatchDisabled(options?.truckCapabilityById?.[compatibility.truck_id])
  ));

  if (!compatibilities.length) {
    return {
      deliveryIds,
      errorMessage: buildNoCompatibleTruckMessage({
        associatedProductFilteredTruckCount,
        aspiratorFilteredTruckCount,
        ontarioFilteredTruckCount,
        protectedFilteredTruckCount,
        requiresAspirator: options?.requiresAspirator === true,
        requiresOntario: options?.requiresOntario === true,
        sourceProductLabel: options?.sourceProductLabel || "",
        sourceTruckName: options?.sourceTruckName || "",
      }),
    };
  }

  dispatchDisabledVisibleTruckCount = countDispatchDisabledVisibleTruckIds(
    compatibilities,
    options?.truckCapabilityById || null,
  );

  return {
    blockedReasonByTruckId: addBlockedCompatibilityReasons(
      Object.assign({}, ...candidates.map((candidate) => candidate.blockedReasonByTruckId)),
      compatibilities,
      actionableCompatibilities,
      options?.truckCapabilityById || null,
    ),
    compatibilities,
    actionableCompatibilityByTruckId: buildCompatibilityIndex(actionableCompatibilities),
    compatibilityByTruckId: buildCompatibilityIndex(compatibilities),
    deliveryIds,
    deliveryId: deliveryIds[0] || "",
    message: buildCandidatePoolMessage({
      actionableCompatibilities,
      associatedProductFilteredTruckCount,
      aspiratorFilteredTruckCount,
      compatibilities,
      dispatchDisabledVisibleTruckCount,
      ontarioFilteredTruckCount,
      protectedFilteredTruckCount,
      requiresAspirator: options?.requiresAspirator === true,
      requiresOntario: options?.requiresOntario === true,
      sourceProductLabel: options?.sourceProductLabel || "",
      sourceTruckName: options?.sourceTruckName || "",
    }),
    ontarioFilteredTruckCount,
    requiresLiveRefresh: requiresLiveRefresh || hasLiveRefreshCompatibilityConflict(safeContexts),
  };
}

export function buildReassignmentDragCandidate(contexts = [], options = {}) {
  if (!Array.isArray(contexts) || contexts.length !== 1) {
    return {
      errorMessage: "Ce segment ouvre plusieurs réaffectations. Utilisez le picker existant pour conserver le flux métier actuel.",
    };
  }

  const context = contexts[0];
  const requiresAspirator = options?.requiresAspirator === true;
  const requiresOntario = options?.requiresOntario === true;
  const deliveryId = typeof context?.delivery_id === "string"
    ? context.delivery_id
    : (typeof context?.deliveryId === "string" ? context.deliveryId : "");
  const enrichedCompatibilities = (Array.isArray(context?.compatibilities) ? context.compatibilities : [])
    .map((compatibility) => enrichLockedTruckMismatchWithDompeurOverride(compatibility, options));
  const { associatedProductFilteredTruckCount, aspiratorFilteredTruckCount, blockedReasonByTruckId, compatibilities, ontarioFilteredTruckCount, protectedFilteredTruckCount } = filterVisibleCompatibilities(enrichedCompatibilities, {
    requiresAspirator,
    requiresOntario,
    sourceProductLabel: options?.sourceProductLabel || "",
    truckCapabilityById: options?.truckCapabilityById || null,
  });
  const actionableCompatibilities = compatibilities.filter((compatibility) => (
    isTruckCompatibilityActionable(compatibility)
    && !isTruckDispatchDisabled(options?.truckCapabilityById?.[compatibility.truck_id])
  ));
  const dispatchDisabledVisibleTruckCount = countDispatchDisabledVisibleTruckIds(
    compatibilities,
    options?.truckCapabilityById || null,
  );

  if (!deliveryId) {
    return {
      errorMessage: "Aucune commande exploitable n'est disponible pour ce segment dans le flux actuel.",
    };
  }

  return {
    associatedProductFilteredTruckCount,
    aspiratorFilteredTruckCount,
    blockedReasonByTruckId: addBlockedCompatibilityReasons(
      blockedReasonByTruckId,
      compatibilities,
      actionableCompatibilities,
      options?.truckCapabilityById || null,
    ),
    compatibilities,
    actionableCompatibilityByTruckId: buildCompatibilityIndex(actionableCompatibilities),
    compatibilityByTruckId: buildCompatibilityIndex(compatibilities),
    deliveryId,
    message: buildCandidatePoolMessage({
      associatedProductFilteredTruckCount,
      actionableCompatibilities,
      aspiratorFilteredTruckCount,
      compatibilities,
      dispatchDisabledVisibleTruckCount,
      ontarioFilteredTruckCount,
      protectedFilteredTruckCount,
      requiresAspirator,
      requiresOntario,
      sourceProductLabel: options?.sourceProductLabel || "",
      sourceTruckName: options?.sourceTruckName || "",
    }),
    ontarioFilteredTruckCount,
    protectedFilteredTruckCount,
    requiresLiveRefresh: hasLiveRefreshCompatibilityConflict(contexts),
  };
}

export function resolveEndOfDayLoadSourceProductLabel({
  segment = null,
  editableOrder = null,
  groupSegments = [],
  order = null,
  orders = [],
} = {}) {
  const segmentProduct = normalizeTextValue(segment?.productLabel);
  if (segmentProduct) {
    return segmentProduct;
  }

  const directOrderProduct = normalizeTextValue(order?.grain_type) || normalizeTextValue(editableOrder?.grain_type);
  if (directOrderProduct) {
    return directOrderProduct;
  }

  const groupProducts = [];
  for (const groupSegment of Array.isArray(groupSegments) ? groupSegments : []) {
    const groupSegmentProduct = normalizeTextValue(groupSegment?.productLabel)
      || normalizeTextValue(groupSegment?.businessStep?.orderDetails?.editableOrder?.grain_type);
    if (groupSegmentProduct) {
      groupProducts.push(groupSegmentProduct);
    }
  }

  for (const currentOrder of Array.isArray(orders) ? orders : []) {
    const orderProduct = normalizeTextValue(currentOrder?.grain_type);
    if (orderProduct) {
      groupProducts.push(orderProduct);
    }
  }

  if (!groupProducts.length) {
    return "";
  }

  const uniqueProductKeys = new Set(groupProducts.map((value) => normalizeProductKey(value)));
  return uniqueProductKeys.size === 1 ? groupProducts[0] : "";
}

export function isTruckCompatibleForEndOfDayLoadProduct(truckLike = null, sourceProductLabel = "", {
  hasAssociatedTruckForProduct = false,
} = {}) {
  const normalizedProduct = normalizeTextValue(sourceProductLabel);
  if (!normalizedProduct || !hasAssociatedTruckForProduct) {
    return true;
  }

  const preferredProduct = resolveTruckPreferredProduct(truckLike);
  if (!preferredProduct) {
    return false;
  }
  return isTruckPreferredForProduct(truckLike, normalizedProduct);
}

function resolveEndOfDayLoadTargetTruckCapability(target = null, truckCapabilityById = null) {
  const truckId = normalizeTextValue(target?.truckId || target?.truck_id);
  const rawData = target?.truck_raw_data && typeof target.truck_raw_data === "object"
    ? target.truck_raw_data
    : (target?.truckRawData && typeof target.truckRawData === "object" ? target.truckRawData : null);

  return truckCapabilityById?.[truckId] || {
    aspirator: firstNonEmptyText(target?.aspirator, rawData?.aspirator, rawData?.Aspirateur),
    isInitialPreloadEnabled: resolveBooleanLike([
      target?.isInitialPreloadEnabled,
      target?.is_initial_preload_enabled,
      rawData?.is_initial_preload_enabled,
      rawData?.pool_preload,
      rawData?.["Pool preload"],
      rawData?.["Pool Preload"],
    ], true),
    isDompeur: resolveBooleanLike([
      target?.isDompeur,
      target?.is_dompeur,
      rawData?.is_dompeur,
      rawData?.Dompeur,
    ], false),
    isTruckProtected: resolveBooleanLike([
      target?.isTruckProtected,
      target?.is_truck_protected,
      rawData?.is_truck_protected,
    ], false),
    ontario: firstNonEmptyText(target?.ontario, rawData?.ontario, rawData?.Ontario),
    preferred_grain_type: firstNonEmptyText(target?.preferredGrainType, target?.preferred_grain_type),
    truckType: firstNonEmptyText(target?.truckType, target?.truck_type, target?.type, rawData?.truck_type, rawData?.type, rawData?.Type),
    truck_raw_data: rawData,
  };
}

export function filterEndOfDayLoadTargetTrucks(targets = [], {
  excludeDompeurTargets = false,
  // Liste source du Chargé PM : la protection compte à la date cible, que le serveur vérifie.
  includeProtected = false,
  requiresAspirator = false,
  requiresInitialPreloadEnabled = false,
  requiresOntario = false,
  sourceProductLabel = "",
  truckCapabilityById = null,
} = {}) {
  const normalizedProduct = normalizeTextValue(sourceProductLabel);

  const activeTargets = (Array.isArray(targets) ? targets : []).map((target) => {
    if (target?.isDispatchDisabled === true || target?.is_dispatch_disabled === true || target?.isActive === false) {
      return null;
    }

    const truckId = normalizeTextValue(target?.truckId || target?.truck_id);
    if (!truckId) {
      return null;
    }

    return {
      target,
      truckCapability: resolveEndOfDayLoadTargetTruckCapability(target, truckCapabilityById),
    };
  }).filter(Boolean);

  const hasAssociatedTruckForProduct = hasPreferredTruckForProduct(
    activeTargets.map(({ truckCapability }) => truckCapability),
    normalizedProduct,
  );

  return activeTargets.filter(({ truckCapability }) => {
    if (!includeProtected && isTruckProtected(truckCapability)) {
      return false;
    }

    if (excludeDompeurTargets && isTruckDompeurType(truckCapability)) {
      return false;
    }

    if (requiresAspirator && !hasTruckAspirator(truckCapability)) {
      return false;
    }

    if (requiresOntario && !hasTruckOntario(truckCapability)) {
      return false;
    }

    if (requiresInitialPreloadEnabled && !hasTruckInitialPreloadEnabled(truckCapability)) {
      return false;
    }

    if (!normalizedProduct) {
      return true;
    }

    return isTruckCompatibleForEndOfDayLoadProduct(truckCapability, normalizedProduct, {
      hasAssociatedTruckForProduct,
    });
  }).map(({ target }) => target);
}

export function filterEndOfDayLoadSourceTrucks(targets = [], { keepTruckId = "", sourceProductLabel = "" } = {}) {
  const activeTargets = (Array.isArray(targets) ? targets : []).filter((target) => {
    const truckId = normalizeTextValue(target?.truckId || target?.truck_id);
    return Boolean(
      truckId
      && target?.isActive !== false
      && target?.is_active !== false
      && target?.isDispatchDisabled !== true
      && target?.is_dispatch_disabled !== true,
    );
  });
  // Même règle produit que les camions cibles ; le camion déjà choisi ou imposé reste affiché.
  const hasAssociatedTruckForProduct = hasPreferredTruckForProduct(activeTargets, sourceProductLabel);
  return activeTargets.filter((target) => (
    normalizeTextValue(target?.truckId || target?.truck_id) === normalizeTextValue(keepTruckId)
    || isTruckCompatibleForEndOfDayLoadProduct(target, sourceProductLabel, { hasAssociatedTruckForProduct })
  ));
}

// Listes de transfert : une remorque sans camion ce jour-là (attelage daté « missing ») reste listée, grisée,
// avec le motif des cibles de réaffectation, et n'est jamais choisie d'office.
export function isTransferTargetTractorMissing(target) {
  return target?.isTractorMissing === true || target?.tractor_status === "missing";
}

export function transferTargetLabel(target) {
  const name = normalizeTextValue(target?.truckName || target?.truck_name) || normalizeTextValue(target?.truckId || target?.truck_id);
  return isTransferTargetTractorMissing(target) ? `${name} — Camion à choisir` : name;
}

export function resolveSelectableTransferTarget(targets = [], preferredTruckId = "") {
  const selectable = (Array.isArray(targets) ? targets : []).filter((target) => !isTransferTargetTractorMissing(target));
  return selectable.find((target) => target.truckId === preferredTruckId) || selectable[0] || null;
}

// Le produit n'est nommé que si le filtre du produit associé a vidé la liste : countWithoutProduct compte la même liste
// sans cette règle.
export function buildEndOfDayLoadTargetTrucksEmptyMessage(sourceProductLabel = "", { countWithoutProduct = 0 } = {}) {
  const normalizedProduct = normalizeTextValue(sourceProductLabel);
  if (normalizedProduct && countWithoutProduct > 0) {
    return `Aucun camion actif associé au produit ${normalizedProduct} n'est disponible pour cette action.`;
  }
  return "Aucun camion actif disponible pour cette action.";
}

// Camions et remorques indépendants : le Chargé PM reste dans sa remorque, livrée par sa ligne à la date cible.
export function resolveEndOfDayLoadTrailerLabel(truck = null) {
  return normalizeTextValue(truck?.compartmentName || truck?.truckName || truck?.truckId);
}

// Un camion sans remorque (dompeur) garde son nom ; sinon le Chargé PM nomme sa remorque.
function isEndOfDayLoadTruckWithoutTrailer(truck = null) {
  return Boolean(truck?.truckId && !truck?.compartmentName);
}

export function resolveEndOfDayLoadTrailerFieldLabel(truck = null, role = "cible") {
  return `${isEndOfDayLoadTruckWithoutTrailer(truck) ? "Camion" : "Remorque"} ${role}`;
}

// Pour les phrases : « La remorque source W26 », « de la remorque cible W26 », « cette remorque ».
export function resolveEndOfDayLoadTrailerWords(truck = null, role = "source") {
  const name = `${role} ${resolveEndOfDayLoadTrailerLabel(truck)}`.trim();
  return isEndOfDayLoadTruckWithoutTrailer(truck)
    ? { The: `Le camion ${name}`, the: `le camion ${name}`, of: `du camion ${name}`, this: "ce camion" }
    : { The: `La remorque ${name}`, the: `la remorque ${name}`, of: `de la remorque ${name}`, this: "cette remorque" };
}

function findEndOfDayLoadTruckTarget(truckId = "", truckReassignmentTargets = []) {
  const normalizedTruckId = normalizeTextValue(truckId);
  return normalizedTruckId
    ? (Array.isArray(truckReassignmentTargets) ? truckReassignmentTargets : [])
      .find((item) => normalizeTextValue(item?.truckId || item?.truck_id) === normalizedTruckId) || null
    : null;
}

// Camions et remorques indépendants : le Chargé PM se nomme par sa remorque, sinon par le camion.
function resolveEndOfDayLoadTruckLabel(truckId = "", truckReassignmentTargets = []) {
  const normalizedTruckId = normalizeTextValue(truckId);
  if (!normalizedTruckId) {
    return "";
  }

  const target = findEndOfDayLoadTruckTarget(normalizedTruckId, truckReassignmentTargets);
  return firstNonEmptyText(target?.compartmentName, target?.truckName, target?.truck_name, target?.label, normalizedTruckId);
}

function resolveEndOfDayLoadMarkerByKey(markerKey = "", row = null) {
  const normalizedMarkerKey = normalizeTextValue(markerKey);
  if (!normalizedMarkerKey) {
    return null;
  }

  const assignment = row?.assignment && typeof row.assignment === "object" ? row.assignment : null;
  const markerLists = [
    ...(Array.isArray(assignment?.end_of_day_loads) ? assignment.end_of_day_loads : []),
    ...(Array.isArray(assignment?.end_of_day_load_source_preloads) ? assignment.end_of_day_load_source_preloads : []),
  ];

  return markerLists.find((marker) => normalizeTextValue(marker?.marker_key) === normalizedMarkerKey) || null;
}

export function resolveEndOfDayLoadInitialTruckLabel({
  row = null,
  segment = null,
  truckReassignmentTargets = [],
} = {}) {
  if (segment?.isEndOfDayLoadSourcePreload === true) {
    // Ligne du camion qui livre : le camion initial est la source du PM, souvent un autre camion.
    const sourceTruckId = normalizeTextValue(segment?.endOfDayLoadSourceTruckId);
    return firstNonEmptyText(
      findEndOfDayLoadTruckTarget(sourceTruckId, truckReassignmentTargets)?.compartmentName,
      segment?.endOfDayLoadSourceTruckName,
      sourceTruckId ? resolveEndOfDayLoadTruckLabel(sourceTruckId, truckReassignmentTargets) : "",
      row?.truckLabel,
      row?.assignment?.truck_name,
      row?.assignment?.driver_name,
      resolveEndOfDayLoadTruckLabel(row?.assignmentId, truckReassignmentTargets),
    );
  }

  const marker = resolveEndOfDayLoadMarkerByKey(segment?.endOfDayLoadMarkerKey, row);
  const sourceTruckIds = new Set(
    (Array.isArray(marker?.items) ? marker.items : [])
      .map((item) => normalizeTextValue(item?.source_truck_id))
      .filter(Boolean),
  );

  if (sourceTruckIds.size === 1) {
    return resolveEndOfDayLoadTruckLabel([...sourceTruckIds][0], truckReassignmentTargets);
  }

  return "";
}

export function resolveEndOfDayLoadTargetTruckLabel({
  row = null,
  segment = null,
  truckReassignmentTargets = [],
} = {}) {
  // Ligne du camion qui livre : c'est lui, la cible.
  if (segment?.isEndOfDayLoadSourcePreload === true) {
    return firstNonEmptyText(row?.assignment?.compartment_name, row?.truckLabel, row?.assignment?.truck_name, resolveEndOfDayLoadTruckLabel(row?.assignmentId, truckReassignmentTargets));
  }
  return firstNonEmptyText(
    findEndOfDayLoadTruckTarget(segment?.endOfDayLoadTargetTruckId || segment?.end_of_day_load_target_truck_id, truckReassignmentTargets)?.compartmentName,
    segment?.endOfDayLoadTargetTruckName,
    segment?.end_of_day_load_target_truck_name,
    resolveEndOfDayLoadTruckLabel(segment?.endOfDayLoadTargetTruckId || segment?.end_of_day_load_target_truck_id, truckReassignmentTargets),
  );
}

export function buildEndOfDayLoadSegmentEditorEmptyMessage({
  row = null,
  segment = null,
  truckReassignmentTargets = [],
} = {}) {
  return "En cliquant sur Retirer PM, vous choisirez entre la remorque source et la date de livraison cible.";
}
